import type { PageHelp } from '../../help/types';

/** Page guide for `/packages/:code/settings` — identity, locales, subscribers, coverage, publishing the subscribers (spec §9.3, §9.5). */
export const packageSettingsHelp: PageHelp = {
  title: 'Package settings',
  intro:
    'The name, key prefixes and locales of one package, the projects that subscribe to it, and how far it is translated. A change here reaches every subscribing project’s bundles.',
  sections: ['Identity', 'Locales', 'Subscribers and coverage', 'Publishing the subscribers'],
  contexts: {
    'subscribers-dialog': 'Click “Publish subscribers” on the “Subscribers” card.',
  },
  tips: [
    'Only a package owner or an administrator can change this page; for everyone else the fields are disabled and the page is for reading.',
    'Changing the prefixes or the locales counts as a content change for every subscribing project, so their targets set to auto-publish pick it up.',
    'A package is never published on its own: its keys reach the applications inside the bundles of the projects that subscribe to it.',
  ],
  steps: [
    {
      selector: '[data-help="package-identity"]',
      title: 'Edit the package identity',
      body: 'Name, description and key prefixes. There is no Save button: each field saves on its own when you leave it, and a “Saved” message confirms it. A change the server refuses is reported in a message, and the field goes back to the stored value. The fields are disabled unless you are a package owner or an administrator.',
      section: 'Identity',
      i18n: {
        'de-AT': {
          title: 'Identität des Pakets bearbeiten',
          body: 'Name, Beschreibung und Schlüsselpräfixe. Es gibt keine Schaltfläche zum Speichern: Jedes Feld speichert beim Verlassen von selbst, und die Meldung „Saved“ bestätigt es. Eine Änderung, die der Server ablehnt, wird in einer Meldung genannt, und das Feld kehrt zum gespeicherten Wert zurück. Die Felder sind gesperrt, außer Sie sind Paket-Owner oder Administrator.',
        },
        'sr-Latn-RS': {
          title: 'Izmena identiteta paketa',
          body: 'Naziv, opis i prefiksi ključeva. Nema dugmeta za čuvanje: svako polje se samo sačuva kada ga napustite, a poruka „Saved“ to potvrđuje. Izmena koju server odbije prijavljuje se porukom, a polje se vraća na sačuvanu vrednost. Polja su zaključana osim ako ste owner paketa ili administrator.',
        },
      },
    },
    {
      selector: '[data-help="package-name"]',
      title: 'Rename the package',
      body: 'The display name in the package list and in the title of this page. The package code, and with it every URL of the package, stays the same.',
      section: 'Identity',
      i18n: {
        'de-AT': {
          title: 'Paket umbenennen',
          body: 'Der Anzeigename in der Paketliste und im Titel dieser Seite. Der Code des Pakets und damit jede URL des Pakets bleiben gleich.',
        },
        'sr-Latn-RS': {
          title: 'Preimenovanje paketa',
          body: 'Naziv koji se prikazuje u listi paketa i u naslovu ove stranice. Kod paketa, a sa njim i svaki URL paketa, ostaje isti.',
        },
      },
    },
    {
      selector: '[data-help="package-description"]',
      title: 'Describe the package for the AI',
      body: 'Sent to the AI as context whenever it translates a key this package owns, so say what the strings are and where they appear. Without a description the AI only gets the package name.',
      section: 'Identity',
      i18n: {
        'de-AT': {
          title: 'Paket für die KI beschreiben',
          body: 'Wird der KI als Kontext mitgegeben, sobald sie einen Schlüssel dieses Pakets übersetzt; schreiben Sie also, was die Texte sind und wo sie erscheinen. Ohne Beschreibung erhält die KI nur den Paketnamen.',
        },
        'sr-Latn-RS': {
          title: 'Opis paketa za AI',
          body: 'Šalje se AI-ju kao kontekst svaki put kada prevodi ključ ovog paketa, pa napišite šta su ti tekstovi i gde se pojavljuju. Bez opisa AI dobija samo naziv paketa.',
        },
      },
    },
    {
      selector: '[data-help="package-prefixes"]',
      title: 'Change the key prefixes',
      body: 'Comma-separated first segments of the key names this package owns. Adding a prefix another project or package owns is refused. Removing a prefix is only accepted while no key of this package uses it.',
      section: 'Identity',
      i18n: {
        'de-AT': {
          title: 'Schlüsselpräfixe ändern',
          body: 'Durch Beistriche getrennte erste Segmente der Schlüsselnamen, die diesem Paket gehören. Ein Präfix hinzuzufügen, das einem anderen Projekt oder Paket gehört, wird abgelehnt. Ein Präfix lässt sich nur entfernen, solange kein Schlüssel dieses Pakets es verwendet.',
        },
        'sr-Latn-RS': {
          title: 'Izmena prefiksa ključeva',
          body: 'Prvi segmenti imena ključeva koje ovaj paket poseduje, odvojeni zarezom. Dodavanje prefiksa koji pripada drugom projektu ili paketu biva odbijeno. Prefiks se može ukloniti samo dok ga ne koristi nijedan ključ ovog paketa.',
        },
      },
    },
    {
      selector: '[data-help="package-locales"]',
      title: 'Understand package locales',
      body: 'A package key only reaches the locales the package itself carries, even when a subscribing project has more. When projects subscribe, the notice above the list says how many: removing a locale removes those translations from all of their bundles at once.',
      section: 'Locales',
      i18n: {
        'de-AT': {
          title: 'Paketsprachen verstehen',
          body: 'Ein Paketschlüssel erreicht nur die Sprachen, die das Paket selbst führt, auch wenn ein abonnierendes Projekt mehr hat. Haben Projekte abonniert, nennt der Hinweis über der Liste ihre Zahl: Das Entfernen einer Sprache nimmt diese Übersetzungen aus allen ihren Bundles auf einmal.',
        },
        'sr-Latn-RS': {
          title: 'Razumevanje jezika paketa',
          body: 'Ključ paketa stiže samo do jezika koje sam paket ima, čak i kada projekat pretplatnik ima više jezika. Kada postoje pretplatnici, obaveštenje iznad liste navodi koliko ih je: uklanjanje jezika odjednom uklanja te prevode iz svih njihovih izvoznih datoteka.',
        },
      },
    },
    {
      selector: '[data-help="package-locales-select"]',
      title: 'Add or remove a locale',
      body: 'Open the list and select or clear a locale; every change is saved immediately. The source locale, the highlighted chip, cannot be removed and comes back if you clear it. Owners and administrators only.',
      section: 'Locales',
      i18n: {
        'de-AT': {
          title: 'Sprache hinzufügen oder entfernen',
          body: 'Öffnen Sie die Liste und wählen Sie eine Sprache aus oder ab; jede Änderung wird sofort gespeichert. Die Ausgangssprache, der hervorgehobene Chip, lässt sich nicht entfernen und kehrt zurück, wenn Sie sie abwählen. Nur für Owner und Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje ili uklanjanje jezika',
          body: 'Otvorite listu i označite ili poništite jezik; svaka izmena se odmah čuva. Izvorni jezik, istaknuti čip, ne može se ukloniti i vraća se ako ga poništite. Samo za ownere i administratore.',
        },
      },
    },
    {
      selector: '[data-help="package-subscribers"]',
      title: 'See who subscribes',
      body: 'The projects whose bundles include this package’s keys. The list cannot be edited here: a project owner adds or removes the subscription in that project’s settings.',
      section: 'Subscribers and coverage',
      i18n: {
        'de-AT': {
          title: 'Abonnenten ansehen',
          body: 'Die Projekte, deren Bundles die Schlüssel dieses Pakets enthalten. Hier lässt sich die Liste nicht bearbeiten: Ein Projekt-Owner fügt das Abonnement in den Einstellungen des Projekts hinzu oder entfernt es dort.',
        },
        'sr-Latn-RS': {
          title: 'Pregled pretplatnika',
          body: 'Projekti čije izvozne datoteke sadrže ključeve ovog paketa. Lista se ovde ne može menjati: owner projekta dodaje ili uklanja pretplatu u podešavanjima tog projekta.',
        },
      },
    },
    {
      selector: '[data-help="package-coverage"]',
      title: 'Check translation coverage',
      body: 'Per locale, how many translation units are reviewed, draft, machine or still empty. Counted in units, so a plural key counts once per plural form of that locale. An empty cell in a package locale is missing from every subscriber at once.',
      section: 'Subscribers and coverage',
      i18n: {
        'de-AT': {
          title: 'Übersetzungsabdeckung prüfen',
          body: 'Pro Sprache, wie viele Übersetzungseinheiten reviewed, draft, machine oder noch leer sind. Gezählt wird in Einheiten, ein Pluralschlüssel zählt also einmal pro Pluralform dieser Sprache. Eine leere Zelle in einer Paketsprache fehlt bei allen Abonnenten zugleich.',
        },
        'sr-Latn-RS': {
          title: 'Provera pokrivenosti prevoda',
          body: 'Po jeziku, koliko je jedinica prevoda reviewed, draft, machine ili još prazno. Broji se u jedinicama, pa se ključ sa množinom broji jednom po obliku množine tog jezika. Prazna ćelija u jeziku paketa nedostaje svim pretplatnicima odjednom.',
        },
      },
    },
    {
      selector: '[data-help="package-subscribers-publish"]',
      title: 'Publish every subscriber at once',
      body: 'Publishes each subscribing project to its target in the environment this Glossa serves, so a package change reaches the applications without visiting every project. Disabled without subscribers, and for anyone who may not publish the package.',
      section: 'Publishing the subscribers',
      i18n: {
        'de-AT': {
          title: 'Alle Abonnenten auf einmal veröffentlichen',
          body: 'Veröffentlicht jedes abonnierende Projekt auf sein Ziel in der Umgebung, die dieses Glossa bedient — so erreicht eine Paketänderung die Anwendungen, ohne jedes Projekt einzeln aufzurufen. Gesperrt ohne Abonnenten und für alle, die das Paket nicht veröffentlichen dürfen.',
        },
        'sr-Latn-RS': {
          title: 'Objavljivanje svih pretplatnika odjednom',
          body: 'Objavljuje svaki projekat pretplatnik na njegov cilj u okruženju koje ovaj Glossa opslužuje, pa izmena paketa stiže do aplikacija bez obilaska svakog projekta. Zaključano bez pretplatnika i za svakoga ko ne sme da objavljuje paket.',
        },
      },
    },
    {
      selector: '[data-help="package-subscribers-note"]',
      title: 'Add a note',
      body: 'Stored on every release this run creates. Left empty, the releases say the package was published to its subscribers.',
      section: 'Publishing the subscribers',
      context: 'subscribers-dialog',
      i18n: {
        'de-AT': {
          title: 'Eine Notiz hinzufügen',
          body: 'Wird bei jedem Release gespeichert, das dieser Lauf anlegt. Leer gelassen steht dort, dass das Paket an seine Abonnenten veröffentlicht wurde.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje napomene',
          body: 'Čuva se uz svako izdanje koje ovo pokretanje napravi. Ako ostane prazno, izdanja kažu da je paket objavljen pretplatnicima.',
        },
      },
    },
    {
      selector: '[data-help="package-subscribers-run"]',
      title: 'Start the run',
      body: 'Each project is published under its own rules: a reviewed-only target still refuses unreviewed values, a project without a target is left alone, and a project you may not publish is skipped. One refusal does not stop the others.',
      section: 'Publishing the subscribers',
      context: 'subscribers-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Lauf starten',
          body: 'Jedes Projekt wird nach seinen eigenen Regeln veröffentlicht: Ein Ziel nur für geprüfte Werte lehnt ungeprüfte weiterhin ab, ein Projekt ohne Ziel bleibt unberührt, und ein Projekt, das Sie nicht veröffentlichen dürfen, wird übersprungen. Eine Ablehnung hält die anderen nicht auf.',
        },
        'sr-Latn-RS': {
          title: 'Pokretanje',
          body: 'Svaki projekat se objavljuje po sopstvenim pravilima: cilj samo za pregledane vrednosti i dalje odbija nepregledane, projekat bez cilja ostaje netaknut, a projekat koji ne smete da objavite se preskače. Jedno odbijanje ne zaustavlja ostale.',
        },
      },
    },
    {
      selector: '[data-help="package-subscribers-results"]',
      title: 'Read what happened to each project',
      body: 'One row per subscriber: published with its new version, unchanged because the same files are already live, held back by a gate, not allowed, without a target, or failed.',
      section: 'Publishing the subscribers',
      context: 'subscribers-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Lesen, was mit jedem Projekt geschah',
          body: 'Eine Zeile pro Abonnent: veröffentlicht mit neuer Version, unverändert, weil dieselben Dateien schon live sind, von einer Sperre zurückgehalten, nicht erlaubt, ohne Ziel oder fehlgeschlagen.',
        },
        'sr-Latn-RS': {
          title: 'Šta se desilo sa svakim projektom',
          body: 'Jedan red po pretplatniku: objavljen sa novom verzijom, nepromenjen jer su iste datoteke već objavljene, zadržan uslovom, nije dozvoljeno, bez cilja ili neuspešno.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Paketeinstellungen',
      intro:
        'Name, Schlüsselpräfixe und Sprachen eines Pakets, die Projekte, die es abonnieren, und wie weit es übersetzt ist. Eine Änderung hier erreicht die Bundles aller abonnierenden Projekte.',
      sections: {
        Identity: 'Identität',
        Locales: 'Sprachen',
        'Subscribers and coverage': 'Abonnenten und Abdeckung',
        'Publishing the subscribers': 'Die Abonnenten veröffentlichen',
      },
      contexts: {
        'subscribers-dialog': 'Klicken Sie auf der Karte „Subscribers“ auf „Publish subscribers“.',
      },
      tips: [
        'Nur Paket-Owner oder Administratoren können diese Seite ändern; für alle anderen sind die Felder gesperrt und die Seite dient zum Lesen.',
        'Eine Änderung der Präfixe oder Sprachen gilt für jedes abonnierende Projekt als inhaltliche Änderung, Ziele mit automatischer Veröffentlichung greifen sie daher auf.',
        'Ein Paket wird nie eigenständig veröffentlicht: Seine Schlüssel erreichen die Anwendungen in den Bundles der Projekte, die es abonnieren.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Podešavanja paketa',
      intro:
        'Naziv, prefiksi ključeva i jezici jednog paketa, projekti koji su pretplaćeni na njega i koliko je preveden. Izmena ovde stiže do izvoznih datoteka svih projekata pretplatnika.',
      sections: {
        Identity: 'Identitet',
        Locales: 'Jezici',
        'Subscribers and coverage': 'Pretplatnici i pokrivenost',
        'Publishing the subscribers': 'Objavljivanje pretplatnika',
      },
      contexts: {
        'subscribers-dialog': 'Kliknite na „Publish subscribers“ na kartici „Subscribers“.',
      },
      tips: [
        'Ovu stranicu može da menja samo owner paketa ili administrator; za sve ostale polja su zaključana i stranica služi za čitanje.',
        'Izmena prefiksa ili jezika računa se kao izmena sadržaja za svaki projekat pretplatnik, pa je njihovi ciljevi sa automatskim objavljivanjem preuzimaju.',
        'Paket se nikad ne objavljuje samostalno: njegovi ključevi stižu do aplikacija u izvoznim datotekama projekata koji su na njega pretplaćeni.',
      ],
    },
  },
};
